import {
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnInit,
	Output,
	ViewChild,
	ViewEncapsulation,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ZkNotificationService } from '@zenklub/ng-ui';
import { fabric } from 'fabric';

import {
	ApiError,
	ApiErrorResponse,
} from '../../../interfaces/error.interface';
import {
	IProfessionalProfile,
	IProfessionalProfileUpdate,
} from '../../../interfaces/professionalprofile.interface';
import { UserService } from '../../../services/user.service';

enum View {
	closed = 'CLOSED',
	rotate = 'ROTATE',
	crop = 'CROP',
	removeBackground = 'REMOVE_BACKGROUND',
	preview = 'PREVIEW',
	saving = 'SAVING',
	success = 'SUCCESS',
	error = 'ERROR',
}

const CANVAS_DEFAULT_SIDE_SIZE = 2000;

const staticImageOpts: fabric.IImageOptions = {
	evented: false,
	hasBorders: false,
	hasControls: false,
	lockMovementX: true,
	lockMovementY: true,
	lockRotation: true,
	lockScalingX: true,
	lockScalingY: true,
	lockUniScaling: true,
	selectable: false,
};

export type ResponseCallback = (err?: Error | null) => void;
export type FinishCallback = (file: File, cb: ResponseCallback) => void;

@Component({
	selector: 'app-my-account-signature',
	templateUrl: './signature.component.html',
	styleUrls: ['./signature.component.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class MyAccountSignatureComponent implements OnInit {
	@Input() onFinish: FinishCallback;
	@Input() data: IProfessionalProfile;
	@Output() afterUpdateSignature = new EventEmitter<void>();
	@ViewChild('signatureUploadInput')
	uploadSignatureInput: ElementRef;
	viewTypes = View;
	view: View = View.closed;
	private canvas: fabric.Canvas;
	private editsHistory: {
		canvas: string;
		view: View;
		removeColorIntensity: number;
	}[] = [];
	public removeColorIntensity = 0;

	constructor(
		public user: UserService,
		protected translate: TranslateService,
		private notifications: ZkNotificationService
	) {}

	ngOnInit() {
		if (this.data.signatureUrl && !this.data.signatureUrl.includes('?')) {
			this.data.signatureUrl += `?${Date.now().toString()}`;
		}
		this.createCanvas();
	}

	async onFileChangeEvent(event: Event) {
		try {
			const signatureData: string = await new Promise(
				async (resolve, reject) => {
					const target = event.target as HTMLInputElement;
					const reader = new FileReader();
					reader.onload = () => resolve(reader.result as string);
					reader.onerror = (err) => reject(err);
					if (target.files && target.files[0]) {
						reader.readAsDataURL(target.files[0]);
					} else {
						reject(new Error('Invalid File'));
					}
				}
			);
			await this.loadSignatureAndPlaceIt(signatureData);
			await this.changeView(View.rotate);
		} catch (err) {
			await this.changeView(View.error);
		}
	}

	async saveAndFinish() {
		this.changeView(View.saving);

		const DESIRED_SIGNATURE_WIDTH = 600;
		const signature = this.getSignature().set({ strokeWidth: 0 });

		if (signature) {
			const dataURL = signature.toDataURL({
				format: 'png',
				multiplier: DESIRED_SIGNATURE_WIDTH / signature.getScaledWidth(),
			});
			const binary = atob(dataURL.split(',')[1]);
			const charCodeArray = binary.split('').map((i) => i.charCodeAt(0));
			const file = new File([new Uint8Array(charCodeArray)], 'signature.png', {
				type: 'image/png',
			});
			this.uploadSignature(file);
		}
	}

	uploadSignature(file: File) {
		this.user.uploadSignature(file).subscribe(
			(result) => {
				this.user.getProfileData().then(async (userDt) => {
					userDt.signatureUrl = result;
					this.data.signatureUrl = `${result}?${Date.now()}`;
					this.saveUpdatedProfile(userDt);
				});
			},
			async (error) => {
				this.notifications.showError((error.error as ApiError).messageI18n);
				await this.changeView(View.error);
			}
		);
	}

	saveUpdatedProfile(upData: IProfessionalProfile): void {
		const data: IProfessionalProfileUpdate = {
			...Object.assign({}, upData),
			spokenLanguages: upData.spokenLanguages.map((lang) => lang.id),
			ethnicity: upData.ethnicity ? upData.ethnicity.id : undefined,
			tags: upData.tags.map((tag) => tag.id),
		};
		[
			'active',
			'createdAt',
			'fullName',
			'id',
			'isOnline',
			'isVisible',
			'types',
			'updatedAt',
			'_t',
		].forEach((key) => {
			delete data[key];
		});

		this.user.updateProfileData(data).subscribe(
			() => {
				this.changeView(View.success);
				this.afterUpdateSignature.emit();
			},
			(error: ApiErrorResponse) => {
				this.notifications.showError((error.error as ApiError).messageI18n);
				this.changeView(View.error);
			}
		);
	}

	back(steps = 1) {
		const historyLength = this.editsHistory.length;
		if (historyLength > 0) {
			const back = historyLength - steps <= 0 ? 0 : historyLength - steps;
			const { view, removeColorIntensity, canvas } = this.editsHistory[back];
			this.editsHistory = this.editsHistory.slice(0, back);
			this.view = view;
			this.removeColorIntensity = removeColorIntensity;

			this.canvas.clear().loadFromJSON(canvas, () => {
				const activeObj = this.canvas.getActiveObject();

				this.canvas.setDimensions(
					{ width: this.canvas.getWidth(), height: this.canvas.getHeight() },
					{ backstoreOnly: true }
				);

				if (activeObj) {
					this.canvas.setActiveObject(this.getObjectByName(activeObj.name));
				}

				this.canvas.renderAll();
			});
		}
	}

	public async close() {
		await this.changeView(View.closed);
	}

	public changeView = async (nextView: View) => {
		this.editsHistory.push({
			canvas: this.canvas.toJSON([
				'name',
				'_activeObject',
				'_controlsVisibility',
				'width',
				'height',
				'strokeDashArray',
				'borderColor',
				'cornerColor',
				'fill',
				'hasRotatingPoint',
				'stroke',
				'strokeWidth',
				'cornerSize',
				'cornerStyle',
				...Object.keys(staticImageOpts),
			]),
			removeColorIntensity: this.removeColorIntensity,
			view: this.view,
		});

		switch (this.view) {
			case View.rotate:
				await this.reloadSignature();
				break;
			case View.crop:
				await this.crop();
				break;
		}

		switch (nextView) {
			case View.rotate:
				this.view = View.rotate;
				this.addRotateControlsOnImage();
				break;
			case View.closed:
				this.canvas.clear().setDimensions(
					{
						width: CANVAS_DEFAULT_SIDE_SIZE,
						height: CANVAS_DEFAULT_SIDE_SIZE,
					},
					{ backstoreOnly: true }
				);
				this.view = View.closed;
				this.editsHistory = [];
				this.removeColorIntensity = 0;
				break;
			case View.crop:
				this.view = View.crop;
				this.drawCropRectangle();
				break;
			case View.removeBackground:
				this.view = View.removeBackground;
				this.addRemoveColorFilter();
				break;
			case View.preview:
				this.view = View.preview;
				this.buildPreview();
				break;
			case View.saving:
				this.view = View.saving;
				break;
			case View.success:
				this.view = View.success;
				setTimeout(() => this.changeView(View.closed), 2000);
				break;
			case View.error:
				this.view = View.error;
				setTimeout(() => this.back(2), 4000);
				break;
		}

		this.canvas.renderAll();
	};

	async crop() {
		const signature = this.getSignature();
		if (signature) {
			const cropRectangle = this.getCropRect();
			const croppedImageDataURL = signature.toDataURL({
				height: cropRectangle.getScaledHeight(),
				left: cropRectangle.get('left') - signature.get('left'),
				top: cropRectangle.get('top') - signature.get('top'),
				width: cropRectangle.getScaledWidth(),
			});
			this.canvas
				.setActiveObject(cropRectangle)
				.discardActiveObject()
				.remove(cropRectangle)
				.remove(signature)
				.renderAll();
			await this.loadSignatureAndPlaceIt(croppedImageDataURL);
		}
	}

	updateRemoveColorFilterIntensity(value: number) {
		const signature = this.getSignature();
		if (signature) {
			if (signature.filters) {
				signature.filters[0].setOptions({ color: 'white', distance: value });
			} else {
				signature.filters = [];
			}
			signature.applyFilters();
			this.canvas.renderAll();
			this.removeColorIntensity = value;
		}
	}

	addRotateControlsOnImage() {
		const signature = this.getSignature();
		if (signature) {
			signature
				.set({
					evented: true,
					hasBorders: true,
					hasControls: true,
					lockRotation: false,
					selectable: true,
					cornerSize: 50,
					cornerStyle: 'circle',
				})
				.setControlsVisibility({
					mt: false,
					mb: false,
					ml: false,
					mr: false,
					bl: false,
					br: false,
					tl: false,
					tr: false,
				});
			this.scaleToFitCanvas(signature, 0.8);
			this.canvas.setActiveObject(signature).renderAll();
		}
	}

	private addRemoveColorFilter() {
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore: we ignore because the typings are incorrect
		const removeColorFilter = new fabric.Image.filters.RemoveColor({
			color: 'white',
			distance: 0,
		});
		const signature = this.getSignature();
		if (signature) {
			signature.filters.push(removeColorFilter);
		}
	}

	drawCropRectangle() {
		const signature = this.getSignature();
		const cropRectangle = new fabric.Rect({
			strokeDashArray: [4, 8],
			borderColor: '#666',
			cornerColor: '#333',
			fill: 'transparent',
			hasRotatingPoint: false,
			height: signature.get('height') / 2,
			stroke: 'black',
			strokeWidth: 2,
			width: signature.get('width') / 2,
			name: 'cropRectangle',
			lockScalingFlip: true,
		});

		this.canvas.add(cropRectangle);
		cropRectangle.scale(signature.scaleX).center().setCoords();
		this.canvas.setActiveObject(cropRectangle).renderAll();
	}

	private createCanvas() {
		const canvas = new fabric.Canvas('canvas', {
			preserveObjectStacking: true,
			selection: false,
			uniScaleTransform: true,
		})
			.setDimensions({
				height: CANVAS_DEFAULT_SIDE_SIZE,
				width: CANVAS_DEFAULT_SIDE_SIZE,
			})
			.setDimensions({ height: 'auto', width: '100%' }, { cssOnly: true })
			.renderAll();

		canvas.on('object:moving', (e) => {
			this.onCanvasMoving(e);
		});

		canvas.on('object:scaling', (e) => {
			this.onCanvasScaling(canvas, e);
		});
		this.canvas = canvas;
		fabric.Object.prototype.set({
			transparentCorners: false,
			borderColor: 'blue',
			cornerColor: 'blue',
			objectCaching: false,
		});
	}

	private onCanvasScaling(canvas: fabric.Canvas, e: fabric.IEvent) {
		const obj = e.target;
		const top = Math.max(0, obj.top);
		const scaleYForTopResize = obj.oCoords.bl.y / obj.height;
		const maxScaleY =
			(canvas.getHeight() - top - obj.strokeWidth * 2) / obj.height;
		const scaleY =
			obj.top < 0 ? scaleYForTopResize : Math.min(obj.scaleY, maxScaleY);
		const left = Math.max(0, obj.left);
		const scaleXForLeftResize = obj.oCoords.br.x / obj.width;
		const maxScaleX =
			(canvas.getWidth() - left - obj.strokeWidth * 2) / obj.width;
		const scaleX =
			obj.left < 0 ? scaleXForLeftResize : Math.min(obj.scaleX, maxScaleX);

		obj.set({
			top,
			left,
			scaleY,
			scaleX,
		});
	}

	private onCanvasMoving(e: fabric.IEvent) {
		const obj = e.target;
		obj.setCoords();
		const boundingRect = obj.getBoundingRect();
		const lowestAcceptableObjectTop =
			obj.canvas.getHeight() - boundingRect.height;
		const maxLeftPositionAcceptable =
			obj.canvas.getWidth() - boundingRect.width;
		obj.set({
			top: Math.max(Math.min(obj.top, lowestAcceptableObjectTop), 0),
			left: Math.max(Math.min(obj.left, maxLeftPositionAcceptable), 0),
		});
	}

	private async buildPreview() {
		const receiptSampleLocation = '/assets/images/receipt-sample.jpg';
		const receiptFabricObj = await fabricImageFromURL(
			receiptSampleLocation,
			staticImageOpts
		);
		const signature = this.getSignature();

		const canvasDimensions = {
			width: CANVAS_DEFAULT_SIDE_SIZE,
			height: CANVAS_DEFAULT_SIDE_SIZE * 1.414,
		};
		this.canvas
			.setDimensions(canvasDimensions, { backstoreOnly: true })
			.add(receiptFabricObj)
			.bringToFront(signature);

		receiptFabricObj.scaleToWidth(this.canvas.getWidth());
		signature.set({ top: 1850, left: 200 }).setCoords().scaleToWidth(375);

		this.canvas.renderAll();
	}

	private async loadSignatureAndPlaceIt(src: string): Promise<void> {
		const signature = await fabricImageFromURL(src, staticImageOpts);
		signature.name = 'signature';
		this.scaleToFitCanvas(signature);
		this.canvas.add(signature).centerObject(signature);
	}

	private async reloadSignature() {
		const currentImage = this.getSignature();
		if (currentImage) {
			await this.loadSignatureAndPlaceIt(currentImage.toDataURL({}));
			this.canvas.remove(currentImage);
		}
	}

	private scaleToFitCanvas(object: fabric.Object, factor = 1) {
		const widthFactor = this.canvas.getWidth() / object.get('width');
		const heightFactor = this.canvas.getHeight() / object.get('height');
		const minFactor = Math.min(widthFactor, heightFactor);

		object
			.scale(minFactor * factor)
			.center()
			.setCoords();
	}

	private getObjectByName(name: string) {
		return this.canvas.getObjects().find((obj) => obj.name === name);
	}

	private getSignature(): fabric.Image {
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		return this.getObjectByName('signature');
	}

	private getCropRect(): fabric.Rect {
		return this.getObjectByName('cropRectangle');
	}
}

async function fabricImageFromURL(
	src: string,
	opts: fabric.IImageOptions
): Promise<fabric.Image> {
	return new Promise((resolve) => fabric.Image.fromURL(src, resolve, opts));
}
